import type { PaigeWatchedFolder, PaigeWatchedFolderChannel } from './paige.global';
import { responseWords } from './problem-words';

/**
 * The job's watched folder through the job's channel (`{engineOrigin}` is `/v1/jobs/{id}`):
 * `GET …/watched-folder`, 404 when no computer watches one for the job, and the job itself for its
 * name. Paige never sets a folder up from here; the computer that can see the folder does
 * (docs/engineering/folder-monitor.md, section 4).
 */
export function watchedFolderChannel(
  engineOrigin: string,
  tenant: string | undefined,
): PaigeWatchedFolderChannel {
  const get = (path: string) =>
    fetch(`${engineOrigin}${path}`, {
      credentials: 'include',
      headers: tenant ? { 'Paige-Tenant': tenant } : {},
    });
  return {
    async read() {
      const response = await get('/watched-folder');
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(await reasonOf(response));
      return folderOf((await response.json()) as FolderRow);
    },
    async job() {
      const response = await get('');
      if (!response.ok) return null;
      const job = (await response.json()) as { id?: string; name?: string };
      return job.id && job.name ? { id: job.id, name: job.name } : null;
    },
  };
}

interface FolderRow {
  device: string;
  path: string;
  state: PaigeWatchedFolder['state'];
  problem?: string | null;
  lastFileName?: string | null;
  lastFileAt?: string | null;
  lastSeenAt?: string | null;
  quiet: boolean;
  sentToday: number;
  jobWaiting?: 'setup' | 'off' | null;
}

function folderOf(row: FolderRow): PaigeWatchedFolder {
  return {
    device: row.device,
    path: row.path,
    state: row.state,
    problem: row.problem ?? null,
    lastFileName: row.lastFileName ?? null,
    lastFileAt: row.lastFileAt ?? null,
    lastSeenAt: row.lastSeenAt ?? null,
    quiet: row.quiet,
    sentToday: row.sentToday,
    jobWaiting: row.jobWaiting ?? null,
  };
}

function reasonOf(response: Response): Promise<string> {
  return responseWords(response, 'Paige could not read the watched folder. Try again in a minute.');
}
